// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Mitglieder-Verwaltung für Admins: Konten anlegen und sehen.
// Das Startpasswort vergibt der Admin; die App zwingt das Mitglied beim
// ersten Anmelden zu einem eigenen (users.must_change_password).
import { pb } from "./pb"

export interface Mitglied {
  id: string
  email: string      // freiwillig — nur fürs Passwort-Zurücksetzen
  username: string   // damit meldet man sich an
  displayName: string
  role: "user" | "admin"
  must_change_password: boolean
  may_curate: boolean
  created: string
}

export function istAdmin(): boolean {
  return pb.authStore.record?.role === "admin"
}

/** Darf den gemeinsamen Katalog pflegen (Videos für alle)? Admins immer. */
export function darfKuratieren(): boolean {
  const r = pb.authStore.record
  return r?.role === "admin" || r?.may_curate === true
}

/** Alle Konten — die Leseregel gibt sie nur Admins heraus.
 *  requestKey null: Übersicht und Mitglieder-Liste laden gleichzeitig,
 *  sonst storniert PocketBase die zweite identische Anfrage. */
export async function ladeMitglieder(): Promise<Mitglied[]> {
  return pb.collection("users").getFullList<Mitglied>({ sort: "displayName", batch: 200, requestKey: null })
}

export interface NeuesMitglied {
  username: string
  email: string      // darf leer bleiben (Thors Wunsch Runde 16)
  displayName: string
  passwort: string
  admin: boolean
  kurator: boolean
}

/** Benutzernamen-Vorschlag aus dem Namen: klein, ohne Umlaute, ohne Leerzeichen.
 *  `vergeben` = schon benutzte Namen; dann hängt eine Zahl an (patricia2). */
export function benutzernameAus(name: string, vergeben: readonly string[] = []): string {
  const rein = name
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 38)
  if (!rein) return ""
  const belegt = new Set(vergeben)
  if (!belegt.has(rein)) return rein
  for (let n = 2; n < 100; n++) if (!belegt.has(rein + n)) return rein + n
  return rein
}

export async function legeMitgliedAn(m: NeuesMitglied): Promise<Mitglied> {
  const email = m.email.trim()
  return pb.collection("users").create<Mitglied>({
    username: m.username.trim().toLowerCase(),
    // Leeres E-Mail-Feld gar nicht erst mitschicken: PocketBase prüft sonst
    // das Format von "" und lehnt ab
    ...(email ? { email, emailVisibility: true } : {}),
    password: m.passwort,
    passwordConfirm: m.passwort,
    displayName: m.displayName.trim(),
    role: m.admin ? "admin" : "user",
    may_curate: m.kurator,
    must_change_password: true,
    // verified setzt nur der Superuser — die App verschickt ohnehin keine
    // Bestätigungsmails, und die Anmeldung prüft das Feld nicht
    weekly_goal: 0,
    default_rest_sec: 90,
  })
}

/** Rolle, Katalog-Recht oder Name eines Mitglieds ändern (nur Admins). */
export async function aendereMitglied(id: string, patch: {
  displayName?: string
  username?: string
  role?: "user" | "admin"
  may_curate?: boolean
}): Promise<Mitglied> {
  return pb.collection("users").update<Mitglied>(id, patch)
}

/** Neues Startpasswort setzen — die Person muss es beim nächsten Anmelden ersetzen.
 *  Läuft über eine eigene Route: PocketBase verlangt beim normalen Weg das ALTE
 *  Passwort, und das kennt die Verwaltung bei einem fremden Konto nicht
 *  (siehe pb_hooks/startpasswort.pb.js). */
export async function setzeStartpasswort(id: string, passwort: string): Promise<void> {
  await pb.send("/api/fithub/startpasswort", {
    method: "POST",
    body: { mitglied: id, passwort },
  })
}

/** Konto endgültig löschen — die Kaskade nimmt alle Daten dieser Person mit.
 *  Das eigene Konto lässt die Regel nicht zu (Migration 24). */
export async function loescheMitglied(id: string): Promise<void> {
  await pb.collection("users").delete(id)
}

/** Nur die harten Daten: Adresse, Benutzername, Startpasswort. Für den Fall,
 *  dass die Person schon Bescheid weiß. Die Adresse kommt aus dem Browser,
 *  damit hier keine Domain fest verdrahtet ist. */
export function zugangsText(benutzer: string, passwort: string): string {
  return [
    "Deine Zugangsdaten für fitHub",
    "",
    `Adresse: ${window.location.origin}`,
    `Benutzername: ${benutzer}`,
    `Startpasswort: ${passwort}`,
    "",
    ...hinweise(),
  ].join("\n")
}

/** Die komplette Einladung — für Leute, die noch nichts von ihrem Glück wissen.
 *  Geschrieben aus Thors Sicht, denn er verschickt sie. Bewusst ohne Emoji:
 *  wer welche mag, tippt sie beim Einfügen selbst dazu. */
export function einladungsText(benutzer: string, passwort: string, name = ""): string {
  const anrede = name.trim() ? `Hey ${name.trim()},` : "Hey,"
  return [
    anrede,
    "",
    "ich hab dir einen Zugang zu fitHub angelegt – meinem eigenen kleinen",
    "Trainings-Tagebuch. Läuft auf meinem eigenen Server: keine Werbung,",
    "kein Konzern, keine Daten für irgendwen.",
    "",
    "Was du damit machen kannst:",
    "– dir einen fertigen Trainingsplan aussuchen, von Kraft über Hyrox bis Cheerleading",
    "– dich im Studio Übung für Übung durchführen lassen, mit Pausen-Timer",
    "– jeden Plan auf dich anpassen: Übungen tauschen, Sätze ändern, alles.",
    "  Das ändert nur DEINE Fassung, der ursprüngliche Plan bleibt für alle wie er war",
    "– sehen, wie es über die Wochen vorangeht – da wächst eine Pflanze mit,",
    "  je öfter du trainierst, und ein paar andere kleine Späße sind auch drin",
    "– im Feed sehen, wenn jemand anderes trainiert hat. Ohne Namen: jeder",
    "  bekommt einen Fantasie-Namen, niemand sieht, wer dahintersteckt",
    "",
    "Deine Sätze, Gewichte und dein Körpergewicht sieht niemand außer dir.",
    "Auch ich nicht.",
    "",
    `Adresse: ${window.location.origin}`,
    `Benutzername: ${benutzer}`,
    `Startpasswort: ${passwort}`,
    "",
    ...hinweise(),
    "",
    "Probier dich einfach mal aus. Und wenn dir was fehlt oder du eine Idee",
    "hast – sag mir Bescheid, ich bau das ein.",
  ].join("\n")
}

function hinweise(): string[] {
  return [
    "Beim ersten Anmelden vergibst du dein eigenes Passwort – danach ist das Startpasswort wertlos.",
    "Tipp: In Safari über „Teilen“ → „Zum Home-Bildschirm“, dann liegt fitHub als App auf dem Handy.",
  ]
}

/** Startpasswort-Vorschlag: leicht vorzulesen, erfüllt die Regeln
 *  (≥10 Zeichen, Ziffer, Sonderzeichen). Ohne verwechselbare Zeichen. */
export function passwortVorschlag(): string {
  const woerter = ["Tanne", "Moos", "Fuchs", "Eule", "Igel", "Anker", "Feder", "Kiesel", "Wiese", "Funke"]
  const zufall = new Uint32Array(3)
  crypto.getRandomValues(zufall)
  const w1 = woerter[zufall[0] % woerter.length]
  const w2 = woerter[zufall[1] % woerter.length]
  const zahl = 10 + (zufall[2] % 90)
  return `${w1}-${w2}${zahl}!`
}

export function mussPasswortAendern(): boolean {
  return Boolean(pb.authStore.record?.must_change_password)
}
